import { Injectable } from '@angular/core';
import { SessionService } from '@monsido/ng2/core/session/session.service';
import { ApiService } from '../api-service/api.service';
import { DemoStatsRepoService } from '@client/app/modules/demodata/endpoints/pages/demo-stats-repo/demo-stats-repo.service';
import { StatisticsEntryPageModel } from './statistics-entry-page-model';
import { StatisticsCity, StatisticsLocation, StatisticsPage, StatisticsRegion, StatisticsVisitor } from '@client/app/modules/statistics/interfaces/statistics-vistor.interface';
import { PageModel } from '@client/app/models/page-model-factory.service';
import { MonTableCollection } from '@monsido/ng2/models/table-collection.interface';
import { StatisticsDownload } from '@client/app/modules/statistics/interfaces/statistics-download.interface';
import { StatisticsKeyword } from '@client/app/modules/statistics/interfaces/statistics-keyword.interface';
import { StatisticsSummary } from '@client/app/modules/statistics/interfaces/statistics-summary.interface';
import { StatisticsSiteSearchKeyword } from '@client/app/modules/statistics/interfaces/statistics-site-search-keyword.interface';
import { CustomerHasPlanPipe } from '@monsido/ng2/shared/pipes/customer-has-plan/customer-has-plan.pipe';
import { KeywordsStatisticsResult } from '@monsido/ng2/angularjs-providers/stats-repo/stats-repo.types';
import { HttpContextToken } from '@angular/common/http';
import { contextTokens } from '@monsido/ng2/services/request-auxiliary/request-auxiliary.service';
import { RequestParamsType } from '@monsido/ng2/external/http/options';
import { BaseRepo, BaseRepoRequestType } from '../base-repo/base-repo';

type StateRepoRequestType = RequestParamsType & BaseRepoRequestType;
@Injectable({
    providedIn: 'root',
})
export class StatsRepoService extends BaseRepo {
    private static readonly URL_PROTOCOL_REGEX = /^https?:\/\//i;

    constructor (
        protected sessionService: SessionService,
        private apiService: ApiService,
        private demoStatsRepoService: DemoStatsRepoService,
        private customerHasPlanPipe: CustomerHasPlanPipe,
    ) {
        super();
    }

    getResolutions (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<StatisticsVisitor[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getResolutions();
        } else {
            params = this.setGroupId(params);
            return this.apiService.get<StatisticsVisitor[]>('domains/' + this.getDomainId(params.domain_id) + '/statistics/resolutions', params, {}, contextToken);
        }
    }

    getOs (params: StateRepoRequestType, contextToken?: HttpContextToken<unknown>): Promise<StatisticsVisitor[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getOs();
        } else {
            params = this.setGroupId(params);
            return this.apiService.get<StatisticsVisitor[]>('domains/' + this.getDomainId(params.domain_id) + '/statistics/os', params, {}, contextToken);
        }
    }

    getOrganizations (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<StatisticsVisitor[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getOrganizations();
        } else {
            params = this.setGroupId(params);
            return this.apiService.get<StatisticsVisitor[]>('domains/' + this.getDomainId(params.domain_id) + '/statistics/organizations', params, {}, contextToken);
        }
    }

    getLocations (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<StatisticsLocation[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getLocations();
        } else {
            params = this.setGroupId(params);
            return this.apiService.get<StatisticsLocation[]>('domains/' + this.getDomainId(params.domain_id) + '/statistics/locations', params, {}, contextToken);
        }
    }

    getRegions (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<StatisticsRegion[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getRegions();
        } else {
            params = this.setGroupId(params);
            return this.apiService.get<StatisticsRegion[]>('domains/' + this.getDomainId(params.domain_id) + '/statistics/regions', params, {}, contextToken);
        }
    }

    getCities (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<StatisticsCity[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getCities();
        } else {
            params = this.setGroupId(params);
            return this.apiService.get<StatisticsCity[]>('domains/' + this.getDomainId(params.domain_id) + '/statistics/cities', params, {}, contextToken);
        }
    }

    getLanguages (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<StatisticsVisitor[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getLanguages();
        } else {
            params = this.setGroupId(params);
            return this.apiService.get<StatisticsVisitor[]>('domains/' + this.getDomainId(params.domain_id) + '/statistics/languages', params, {}, contextToken);
        }
    }

    getBrowsers (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<StatisticsVisitor[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getBrowsers();
        } else {
            params = this.setGroupId(params);
            return this.apiService.get<StatisticsVisitor[]>('domains/' + this.getDomainId(params.domain_id) + '/statistics/browsers', params, {}, contextToken);
        }
    }

    getSearchEngines (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<StatisticsVisitor[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getSearchEngines();
        } else {
            params = this.setGroupId(params);
            return this.apiService.get<StatisticsVisitor[]>('domains/' + this.getDomainId(params.domain_id) + '/statistics/search_engines', params, {}, contextToken);
        }
    }

    getReferrers (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<StatisticsVisitor[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getReferrers();
        } else {
            params = this.setGroupId(params);
            return this.apiService.get<StatisticsVisitor[]>('domains/' + this.getDomainId(params.domain_id) + '/statistics/referrers', params, {}, contextToken);
        }
    }

    getPagesWithoutVisits (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<MonTableCollection<PageModel>> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getPagesWithoutVisits(params);
        } else {
            params = this.setGroupId(params);
            params = this.normalizeUrlSearchParam(params);
            return this.apiService.get<MonTableCollection<PageModel>>(
                'domains/' + this.getDomainId(params.domain_id) + '/statistics/pages_without_visits', params, {}, contextToken);
        }
    }

    async getPagesDocumentsWithoutDownloads (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<PageModel[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getPagesDocumentsWithoutDownloads(params);
        } else {
            params = this.setGroupId(params);
            params = this.normalizeUrlSearchParam(params);
            return this.apiService.get<PageModel[]>(
                'domains/' +
                this.getDomainId(params.domain_id) +
                '/statistics/documents_without_downloads', params, {}, contextToken);
        }
    }

    async getDownloadsByUrl (params: StateRepoRequestType = {}): Promise<StatisticsDownload[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getDownloadsByUrl(params);
        }
        params = this.setGroupId(params);
        return this.apiService.post<StatisticsDownload[]>('domains/' + this.getDomainId(params.domain_id) + '/statistics/downloads_by_url', params);
    }

    getPagesByUrl (params:StateRepoRequestType = {}): Promise<StatisticsPage[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getPagesByUrl(params);
        }
        params = this.setGroupId(params);
        return this.apiService.post<StatisticsPage[]>('domains/' + this.getDomainId(params.domain_id) + '/statistics/pages_by_url', params);
    }

    getLeastPopularPages (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<MonTableCollection<StatisticsPage>> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getLeastPopularPages(params);
        } else {
            params = this.setGroupId(params);
            params = this.normalizeUrlSearchParam(params);
            return this.apiService.get<MonTableCollection<StatisticsPage>>(
                'domains/' + this.getDomainId(params.domain_id) + '/statistics/least_popular_pages', params, {}, contextToken);
        }
    }

    getExitPages (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<MonTableCollection<StatisticsPage>> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getExitPages(params);
        } else {
            params = this.setGroupId(params);
            params = this.normalizeUrlSearchParam(params);
            return this.apiService.get<MonTableCollection<StatisticsPage>>('domains/' + this.getDomainId(params.domain_id) + '/statistics/exit_pages', params, {}, contextToken);
        }
    }

    getEntryPages (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<MonTableCollection<StatisticsPage>> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getEntryPages(params);
        } else {
            params = this.setGroupId(params);
            params = this.normalizeUrlSearchParam(params);
            return this.apiService.get<MonTableCollection<StatisticsPage>>('domains/' + this.getDomainId(params.domain_id) + '/statistics/entry_pages', params, {}, contextToken)
                .then((res: MonTableCollection<StatisticsPage>) => this.toEntryPageModels(res));
        }
    }

    getKeywords (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<StatisticsKeyword[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getKeywords();
        } else {
            params = this.setGroupId(params);
            return this.apiService.get<StatisticsKeyword[]>('domains/' + this.getDomainId(params.domain_id) + '/statistics/keywords', params, {}, contextToken);
        }
    }

    getMostPopularPages (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<MonTableCollection<StatisticsPage>> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getMostPopularPages(params);
        } else {
            params = this.setGroupId(params);
            params = this.normalizeUrlSearchParam(params);
            return this.apiService.get<MonTableCollection<StatisticsPage>>(
                'domains/' + this.getDomainId(params.domain_id) + '/statistics/most_popular_pages', params, {}, contextToken,
            );
        }
    }

    getMostDownloads (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<StatisticsDownload[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getMostDownloads(params);
        } else {
            params = this.setGroupId(params);
            params = this.normalizeUrlSearchParam(params);
            return this.apiService.get<StatisticsDownload[]>('domains/' + this.getDomainId(params.domain_id) + '/statistics/downloads', params, {}, contextToken);
        }
    }

    getSummary (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<StatisticsSummary> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getSummary();
        } else {
            params = this.setGroupId(params);
            return this.apiService.get('domains/' + this.getDomainId(params.domain_id) + '/statistics/summary', params, {}, contextToken);
        }
    }

    getSummaryPerDate (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<StatisticsSummary[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getSummaryPerDate(params);
        } else {
            params = this.setGroupId(params);
            return this.apiService.get(
                'domains/' + this.getDomainId(params.domain_id) + '/statistics/summary_per_date', params, {}, contextToken,
            );
        }
    }

    getVisitors (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<{
        referring: StatisticsVisitor[];
        direct: StatisticsVisitor[];
        social: StatisticsVisitor[];
        organic: StatisticsVisitor[];
        paid: StatisticsVisitor[];
    }> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getVisitors(params);
        } else {
            params = this.setGroupId(params);
            return this.apiService.get('domains/' + this.getDomainId(params.domain_id) + '/statistics/visitors', params, {}, contextToken);
        }
    }

    getDevices (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<StatisticsVisitor[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getDevices();
        } else {
            params = this.setGroupId(params);
            return this.apiService.get('domains/' + this.getDomainId(params.domain_id) + '/statistics/devices', params, {}, contextToken);
        }
    }

    getSocials (params: StateRepoRequestType = {}, contextToken?: HttpContextToken<unknown>): Promise<StatisticsVisitor[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getSocials();
        } else {
            params = this.setGroupId(params);
            return this.apiService.get('domains/' + this.getDomainId(params.domain_id) + '/statistics/socials', params, {}, contextToken);
        }
    }

    getSiteSearchKeywords (params: StateRepoRequestType = {}): Promise<MonTableCollection<KeywordsStatisticsResult>> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getSiteSearchKeywords() as unknown as Promise<MonTableCollection<KeywordsStatisticsResult>>;
        }
        params = this.setGroupId(params);
        return this.apiService.get(`domains/${this.getDomainId(params.domain_id)}/statistics/site_search/keywords`, params, {}, contextTokens.NO_PARSE_SEARCH);
    }

    getSiteSearchNoResultsKeywords (params: StateRepoRequestType = {}): Promise<StatisticsSiteSearchKeyword[]> {
        if (this.useSampleData()) {
            return this.demoStatsRepoService.getSiteSearchNoResultsKeywords();
        }
        params = this.setGroupId(params);
        return this.apiService.get(`domains/${this.getDomainId(params.domain_id)}/statistics/site_search/no_results_keywords`, params, {}, contextTokens.NO_PARSE_SEARCH);
    }

    private useSampleData (): boolean {
        const statistics = this.sessionService.domain !== null && this.sessionService.domain.features.statistics;
        return !statistics && this.customerHasPlanPipe.transform('demo');
    }

    private toEntryPageModels (models: MonTableCollection<StatisticsPage>): MonTableCollection<StatisticsPage> {
        for (let i = 0; i < models.length; i++) {
            models[i] = this.toEntryPageModel(models[i]);
        }
        return models;
    }

    private toEntryPageModel (model: StatisticsPage): StatisticsEntryPageModel {
        return new StatisticsEntryPageModel(model);
    }

    private normalizeUrlSearchParam (params: StateRepoRequestType): StateRepoRequestType {
        const search = typeof params.search === 'string'
            ? params.search.trim()
            : '';

        if (!search || !StatsRepoService.URL_PROTOCOL_REGEX.test(search)) {
            return params;
        }

        try {
            const parsedUrl = new URL(search);

            return {
                ...params,
                search: `${parsedUrl.origin}${parsedUrl.pathname}`,
            };
        } catch {
            return params;
        }
    }


}
